import { DeleteOutlined, LoadingOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Checkbox,
  Descriptions,
  Divider,
  Input,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import programApi from '../../api/program.api'
import ContentHeader from '../../components/shared/ContentHeader'
import styles from '../../styles/product.module.scss'
import { programOption } from './model'

const ProgramDetailPage = () => {
  const { programId } = useParams()
  const [program, setProgram] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editedData, setEditedData] = useState(null)
  const [deletedOptionIds, setDeletedOptionIds] = useState([]) // 옵션 삭제 목록
  const navigate = useNavigate()
  const [programType, setProgramType] = useState([])
  useEffect(() => {
    getProgramDetail()
    getProgramType()
  }, [programId])
  useEffect(() => {
    if (program && !isEditing) {
      setEditedData(program)
    }
    console.log('program', program)
  }, [program])

  // 조회
  const getProgramDetail = async () => {
    setIsLoading(true)
    try {
      const res = await programApi.getProgramDetail(programId)
      setProgram(res.data)
    } catch (err) {
      console.log('getPrograms err', err)
      window.alert(`삭제 실패: ${err?.response?.data?.detail?.description}`)
    } finally {
      setIsLoading(false)
    }
  }
  const getProgramType = async () => {
    try {
      const res = await programApi.getProgramType()
      if (res.status === 200) {
        setProgramType(res.data || [])
      } else {
        setProgramType([])
      }
    } catch (error) {
      console.log('getProgramType error', error)
      setProgramType([])
    }
  }

  // 수정 모드 시작
  const handleEditClick = () => {
    // program 데이터를 깊은 복사하여 editedData 초기화 (중첩 객체 불변성 유지)
    setEditedData(JSON.parse(JSON.stringify(program)))
    setDeletedOptionIds([])
    setIsEditing(true)
  }

  // 수정 취소
  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditedData(program)
    setDeletedOptionIds([])
  }

  // 이름, 설명, 타입 수정 데이터 핸들러 (옵션 제외)
  const handleDataChange = (type, value) => {
    setEditedData((prev) => {
      let newName = prev.program?.name
      let newDescription = prev.program?.description
      let currentFeatureTypes =
        prev.programFeatures?.map((f) => f.feature) ?? []

      if (type === 'name') {
        newName = value
      } else if (type === 'description') {
        newDescription = value
      } else if (type === 'featureTypes') {
        currentFeatureTypes = value
      }

      const updatedProgramFeatures = currentFeatureTypes.map((feature) => ({
        feature: feature,
      }))

      return {
        ...prev,
        program: {
          ...prev.program,
          name: newName,
          description: newDescription,
        },
        programFeatures: updatedProgramFeatures,
      }
    })
  }

  // activeDays 변경 (Select 용)
  const handleOptionDayChange = (index, selectedValue) => {
    setEditedData((prev) => {
      const newOptions = [...prev.programOptions]
      // activeDays (복수형) 업데이트
      newOptions[index] = { ...newOptions[index], activeDays: selectedValue }
      return { ...prev, programOptions: newOptions }
    })
  }

  // 옵션명 변경 처리 함수 추가
  const handleOptionNameChange = (index, value) => {
    setEditedData((prev) => {
      const newOptions = [...prev.programOptions]
      newOptions[index] = {
        ...newOptions[index],
        programOptionName: value,
      }
      return { ...prev, programOptions: newOptions }
    })
  }

  // 새 옵션 필드 추가 (id와 activeDays null로 초기화)
  const addProgramOption = () => {
    setEditedData((prev) => ({
      ...prev,
      programOptions: [
        ...(prev.programOptions || []),
        {
          id: undefined,
          activeDays: null,
          programOptionName: '', // 옵션명 필드 추가
        },
      ],
    }))
  }

  // 특정 옵션 필드 제거 (기존 로직 유지 - id 사용)
  const removeProgramOption = (index) => {
    setEditedData((prev) => {
      const optionToRemove = prev.programOptions[index]
      if (
        optionToRemove &&
        typeof optionToRemove.id === 'number' &&
        optionToRemove.id > 0
      ) {
        setDeletedOptionIds((currentDeletedIds) => [
          ...currentDeletedIds,
          optionToRemove.id,
        ])
      }
      const newOptions = [...prev.programOptions]
      newOptions.splice(index, 1)
      return { ...prev, programOptions: newOptions }
    })
  }

  // 수정 저장
  const saveEditedProgram = async () => {
    setIsLoading(true)
    try {
      const validOptions =
        editedData?.programOptions
          ?.map((opt) => ({
            programOptionId: opt.id,
            activeDay: opt.activeDays,
            programOptionName: opt.programOptionName || '',
          }))
          .filter(
            (opt) => typeof opt.activeDay === 'number' && opt.activeDay > 0,
          ) || []

      console.log('validOptions', validOptions)

      const param = {
        programId: programId,
        programName: editedData.program?.name,
        programDescription: editedData.program?.description,
        featureTypes: editedData.programFeatures?.map((f) => f.feature),
        programOptions: validOptions,
        deleteProgramOptionIds: deletedOptionIds,
      }
      console.log('Saving edited param:', param)

      if (validOptions.length === 0) {
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'error',
          title: '유효한 프로그램 기간 옵션을 1개 이상 입력해주세요.',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        })

        setIsLoading(false)
        return
      }
      await programApi.upsertProgram(param)
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: '프로그램이 성공적으로 수정되었습니다.',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      })
      setDeletedOptionIds([])
      setIsEditing(false)
      getProgramDetail()
    } catch (err) {
      console.log('editProgram err', err)
      const errorDescription =
        err?.response?.data?.detail?.description ||
        '프로그램 수정 중 오류가 발생했습니다.'
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'error',
        title: errorDescription,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  // 삭제
  const deleteProgram = async () => {
    if (programId) {
      Swal.fire({
        title: '정말 삭제하시겠습니까?',
        text: '삭제된 프로그램은 복구할 수 없습니다.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#117ffa',
        cancelButtonColor: '#d33',
        confirmButtonText: '확인',
        cancelButtonText: '취소',
      }).then(async (result) => {
        if (result.isConfirmed) {
          setIsLoading(true)
          try {
            const res = await programApi.deleteProgram(programId)
            console.log('res ::', res)
            Swal.fire({
              toast: true,
              position: 'top-end',
              icon: 'success',
              title: '프로그램이 성공적으로 삭제되었습니다.',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
            })
            navigate('/program/manage')
          } catch (err) {
            console.log('deleteProgram err', err)
            const errorDescription =
              err?.response?.data?.detail[0]?.description ||
              '알 수 없는 오류가 발생했습니다. 관리자에게 문의해주세요.'
            Swal.fire({
              toast: true,
              position: 'top-end',
              icon: 'error',
              title: errorDescription,
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
            })
          } finally {
            setIsLoading(false)
          }
        }
      })
    } else {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'error',
        title:
          '프로그램 번호가 없습니다. 잠시만 기다려주시거나 새로고침을 눌러주세요!',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      })
    }
  }

  const disabledSaveButton = () => {
    return (
      editedData.program?.name.length === 0 ||
      editedData.program?.description.length === 0 ||
      editedData.programFeatures?.map((f) => f.feature).length === 0 ||
      editedData.programOptions[0]?.activeDays === 0 ||
      editedData.programOptions[0]?.programOptionName.length === 0 ||
      editedData.programOptions.length === 0
    )
  }

  return (
    <Card title="프로그램 상세 정보" style={{ margin: '20px' }}>
      <ContentHeader title="프로그램 상세 페이지" />
      {isLoading && (
        <Spin
          className={'home-dashboard-loading'}
          indicator={<LoadingOutlined spin />}
          size={'large'}
          style={{ display: 'block', margin: 'auto' }}
        />
      )}

      {!editedData ? (
        <div>관리자에게 문의 부탁드립니다.</div>
      ) : (
        <>
          <div>
            <Descriptions bordered column={1} style={{ marginBottom: 20 }}>
              <Descriptions.Item label="프로그램 번호">
                {editedData.program?.id}
              </Descriptions.Item>
              <Descriptions.Item label="프로그램명">
                <Typography.Text
                  editable={{
                    editing: isEditing,
                    onChange: (newValue) => handleDataChange('name', newValue),
                    icon: <></>,
                    tooltip: false,
                  }}
                  style={{ display: 'block' }}
                >
                  {editedData.program?.name}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="프로그램 설명">
                <Typography.Paragraph
                  editable={{
                    editing: isEditing,
                    onChange: (newValue) =>
                      handleDataChange('description', newValue),
                    icon: <></>,
                    tooltip: false,
                  }}
                  style={{ margin: 0, whiteSpace: 'pre-wrap' }}
                >
                  {editedData.program?.description}
                </Typography.Paragraph>
              </Descriptions.Item>

              <Descriptions.Item label="프로그램 타입">
                {isEditing ? (
                  <Checkbox.Group
                    options={programType.map((item) => ({
                      label: item,
                      value: item,
                    }))}
                    value={
                      editedData.programFeatures?.map((f) => f.feature) ?? []
                    }
                    onChange={(value) =>
                      handleDataChange('featureTypes', value)
                    }
                  />
                ) : (
                  <Space wrap>
                    {editedData.programFeatures?.map((item) => (
                      <Tag key={item.feature}>{item.feature}</Tag>
                    )) ?? '-'}
                  </Space>
                )}
              </Descriptions.Item>
            </Descriptions>

            {/* <Divider />

            <h4>프로그램 타입</h4>
            <div style={{ marginBottom: 20, marginTop: 8 }}>
              {isEditing ? (
                <Checkbox.Group
                  options={programType.map((item) => ({
                    label: item,
                    value: item,
                  }))}
                  value={
                    editedData.programFeatures?.map((f) => f.feature) ?? []
                  }
                  onChange={(value) => handleDataChange('featureTypes', value)}
                />
              ) : (
                <Space wrap>
                  {editedData.programFeatures?.map((item) => (
                    <Tag key={item.feature}>{item.feature}</Tag>
                  )) ?? '-'}
                </Space>
              )}
            </div> */}
          </div>

          <Divider />

          <h4>프로그램 옵션</h4>
          <div style={{ marginBottom: 40, marginTop: 8 }}>
            {isEditing ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                {editedData.programOptions?.map((opt, index) => (
                  <Space key={opt.id ?? `new-${index}`}>
                    <Input
                      placeholder="옵션명 입력"
                      value={opt.programOptionName || ''}
                      onChange={(e) =>
                        handleOptionNameChange(index, e.target.value)
                      }
                      style={{ width: '200px' }}
                    />
                    <Select
                      placeholder="기간 선택"
                      value={opt.activeDays} // 상태의 activeDays(복수) 사용
                      onChange={(value) => handleOptionDayChange(index, value)}
                      style={{ width: '120px' }}
                      options={programOption.map((item) => ({
                        label: item.label,
                        value: parseInt(item.type),
                      }))}
                      allowClear
                    />
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => removeProgramOption(index)}
                      danger
                      size="small"
                    />
                  </Space>
                ))}
                <Button type="dashed" onClick={addProgramOption} block>
                  + 옵션 추가
                </Button>
              </Space>
            ) : (
              <Space direction="vertical">
                {editedData.programOptions?.length > 0
                  ? editedData.programOptions.map((opt, index) => (
                      <div
                        key={opt.id ?? `view-${index}`}
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        <Tag
                          style={{
                            padding: '4px 12px',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          {opt.programOptionName && (
                            <span style={{ fontWeight: 500 }}>
                              {opt.programOptionName}
                            </span>
                          )}
                          <span
                            style={{
                              color: '#666',
                              marginLeft: opt.programOptionName ? '8px' : '0',
                              borderLeft: opt.programOptionName
                                ? '1px solid #d9d9d9'
                                : 'none',
                              paddingLeft: opt.programOptionName ? '8px' : '0',
                            }}
                          >
                            {opt.activeDays ? `${opt.activeDays}일` : '미선택'}
                          </span>
                        </Tag>
                      </div>
                    ))
                  : '-'}
              </Space>
            )}
          </div>

          {/* 버튼 하단 고정 */}
          <div className={`${styles['product-register-fixed-footer']}`}>
            {isEditing ? (
              <>
                <Button
                  type="primary"
                  loading={isLoading}
                  onClick={saveEditedProgram}
                  className={`${styles['product-register-button']} ${styles.save}`}
                  disabled={disabledSaveButton()}
                >
                  저장
                </Button>
                <Button
                  onClick={handleCancelEdit}
                  disabled={isLoading}
                  className={`${styles['product-register-button']} ${styles.cancel}`}
                >
                  취소
                </Button>
              </>
            ) : (
              <>
                <Button
                  loading={isLoading}
                  onClick={handleEditClick}
                  className={`${styles['product-register-button']} ${styles.save}`}
                >
                  수정하기
                </Button>
                <Button
                  onClick={deleteProgram}
                  disabled={isLoading}
                  danger
                  className={`${styles['product-register-button']} ${styles.cancel}`}
                >
                  삭제하기
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </Card>
  )
}

export default ProgramDetailPage
